import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, BigButton } from "@/components/Field";
import { SelectField, TextArea } from "@/components/Bits";
import { TIPOS, parseField, fieldValue, EQ_STATUS } from "@/config/equipamentos";
import { api, errMsg } from "@/lib/api";

const TipoPicker = ({ value, onChange }) => (
  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="equip-form-tipo">
    {Object.entries(TIPOS).map(([k, t]) => (
      <button type="button" key={k} onClick={() => onChange(k)} data-testid={`equip-tipo-${k}`}
        className={`min-h-[76px] rounded-xl border-2 p-2 flex flex-col items-center justify-center gap-1 text-xs font-semibold text-center transition-colors ${value === k ? "border-[#1E4D2B] bg-[#E8F5E9] text-[#1E4D2B]" : "border-[#E2DDD3] hover:border-[#1E4D2B]"}`}>
        <t.icon className="h-6 w-6" /> {t.label}
      </button>
    ))}
  </div>
);

const toForm = (eq) => {
  const f = { tipo: eq.tipo, fazenda_id: eq.fazenda_id, parent_id: eq.parent_id || "", status: eq.status, observacoes: eq.observacoes || "" };
  Object.values(TIPOS).flatMap((t) => t.fields).forEach((x) => { const { key } = parseField(x); f[key] = fieldValue(eq, key) ?? ""; });
  return f;
};

const toBody = (form) => {
  const body = { tipo: form.tipo, fazenda_id: form.fazenda_id, parent_id: form.parent_id || null, status: form.status, observacoes: form.observacoes, specs: {} };
  TIPOS[form.tipo].fields.map(parseField).forEach(({ key, type }) => {
    const raw = form[key];
    const v = raw === "" || raw === undefined ? null : type === "number" ? Number(raw) : raw;
    if (key.startsWith("s.")) { if (v !== null) body.specs[key.slice(2)] = v; } else body[key] = v;
  });
  return body;
};

export const EquipamentoFormDialog = ({ open, onOpenChange, editing, fazendaId, parentId, onSaved }) => {
  const [form, setForm] = useState({});
  const [fazendas, setFazendas] = useState([]);
  const [irmaos, setIrmaos] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editing ? toForm(editing) : { tipo: "trator", fazenda_id: fazendaId || "", parent_id: parentId || "", status: "ativo", observacoes: "" });
    api.get("/fazendas").then(({ data }) => setFazendas(data)).catch(() => {});
  }, [open, editing, fazendaId, parentId]);

  useEffect(() => {
    if (!open || !form.fazenda_id) return setIrmaos([]);
    api.get("/equipamentos", { params: { fazenda_id: form.fazenda_id } })
      .then(({ data }) => setIrmaos(data.filter((e) => e.equipamento_id !== editing?.equipamento_id))).catch(() => {});
  }, [open, form.fazenda_id, editing]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = toBody(form);
      const { data } = editing ? await api.put(`/equipamentos/${editing.equipamento_id}`, body) : await api.post("/equipamentos", body);
      toast.success(editing ? "Equipamento atualizado" : `Equipamento cadastrado (${data.codigo})`);
      onOpenChange(false);
      onSaved(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!form.tipo) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-3xl max-h-[92vh] overflow-y-auto" data-testid="equip-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar equipamento" : "Novo equipamento"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5 mt-2">
          <TipoPicker value={form.tipo} onChange={(tipo) => setForm({ ...form, tipo })} />
          <div className="grid sm:grid-cols-2 gap-4">
            <SelectField label="Fazenda *" id="e-faz" required placeholder="Selecione a fazenda" value={form.fazenda_id}
              onChange={(e) => setForm({ ...form, fazenda_id: e.target.value, parent_id: "" })}
              options={fazendas.map((f) => ({ value: f.fazenda_id, label: `${f.nome} — ${f.cliente_nome || ""}` }))} data-testid="equip-form-fazenda" />
            <SelectField label="Vinculado a (equipamento principal)" id="e-parent" placeholder="Nenhum (direto na fazenda)" value={form.parent_id}
              onChange={set("parent_id")} options={irmaos.map((x) => ({ value: x.equipamento_id, label: `${x.titulo} (${x.codigo})` }))} data-testid="equip-form-parent" />
            {TIPOS[form.tipo].fields.map(parseField).map((f) => f.options ? (
              <SelectField key={f.key} label={f.label} id={`e-${f.key}`} placeholder="Selecione" value={form[f.key] ?? ""} onChange={set(f.key)} options={f.options} data-testid={`equip-form-${f.key.replace("s.", "")}`} />
            ) : (
              <Field key={f.key} label={f.label} id={`e-${f.key}`} type={f.type || "text"} step="any" inputMode={f.type === "number" ? "decimal" : undefined}
                required={f.key === "nome"} value={form[f.key] ?? ""} onChange={set(f.key)} data-testid={`equip-form-${f.key.replace("s.", "")}`} />
            ))}
            <SelectField label="Situação" id="e-status" value={form.status} onChange={set("status")}
              options={Object.entries(EQ_STATUS).map(([value, label]) => ({ value, label }))} data-testid="equip-form-status" />
          </div>
          <TextArea label="Observações" id="e-obs" value={form.observacoes} onChange={set("observacoes")} data-testid="equip-form-observacoes" />
          <BigButton type="submit" disabled={busy} data-testid="equip-form-submit">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Salvar</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
